/**
 * Brittfurn login-prompt-modal, steget före inloggning.
 *
 * När en UTLOGGAD besökare klickar på en gated handling (spara favorit, Välj tyg
 * på en klädbar möbel, Visualisera tapeten) visas en liten stylad modal först.
 * Modalens CTA är en riktig <a href="/customer_authentication/login?return_to=...">
 * så att den befintliga fångst- och replay-logiken (bf-upholstery.rememberReturn
 * + bf-saved.savePending) fortsätter fungera oförändrat.
 *
 * Inloggade besökare ser aldrig modalen, de får den riktiga handlingen direkt.
 *
 * En enda dold modal i DOM:en (snippets/bf-login-modal.liquid, renderad globalt i
 * layout/theme.liquid) återanvänds för alla tre varianter: JS byter bara titel,
 * brödtext och CTA-href per variant.
 *
 * Publikt API (window.bfLoginModal):
 *   open({ variant, returnTo, onProceed }) , öppna modalen
 *   close()                                , stäng
 *   isLoggedIn()                           , samma signal som resten av temat
 *
 * Deklarativ gate: element med data-bf-login-gate="studio|wallpaper" fångas
 * automatiskt när besökaren är utloggad. Favorithjärtat hanteras av bf-saved.js
 * som anropar open() direkt (den äger sin pending-replay).
 */

import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf-login-modal', false);

// Samma signal som bf-saved / bf-upholstery: bfCustomerBootstrap renderas server-side
// bara när kunden är inloggad. Ingen ny auth-check uppfinns här.
function isLoggedIn() {
    return !!window.bfCustomerBootstrap;
}

const FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

let els = null; // cachad DOM
let copy = null; // { favorite, studio, wallpaper }
let lastFocused = null; // fokus att återställa vid stängning
let pendingProceed = null; // körs precis före navigering (t.ex. savePending)

function collect() {
    if (els) return els;
    const root = document.querySelector('[data-bf-login-modal]');
    if (!root) return null;
    els = {
        root,
        card: root.querySelector('.bf-login-modal__card'),
        title: root.querySelector('[data-bf-login-modal-title]'),
        body: root.querySelector('[data-bf-login-modal-body]'),
        cta: root.querySelector('[data-bf-login-modal-cta]')
    };
    return els;
}

function getCopy() {
    if (copy) return copy;
    const node = document.querySelector('[data-bf-login-modal-copy]');
    if (!node) return {};
    try {
        copy = JSON.parse(node.textContent);
    } catch (e) {
        debug.error('kunde inte tolka copy-blocket', e);
        copy = {};
    }
    return copy;
}

function loginHref(returnTo) {
    const rt = returnTo || location.pathname + location.search;
    return '/customer_authentication/login?return_to=' + encodeURIComponent(rt);
}

function onKeydown(e) {
    if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
    }
    if (e.key !== 'Tab') return;
    // Enkel fokusfälla inne i kortet.
    const dom = collect();
    if (!dom) return;
    const items = Array.from(dom.card.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === dom.card
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

function open(opts) {
    opts = opts || {};
    const dom = collect();
    const texts = getCopy();
    if (!dom) {
        // Ingen modal i DOM (t.ex. sida utan layouten): fall tillbaka på direktnavigering
        // så handlingen aldrig blir en tyst no-op.
        debug.warn('modal saknas i DOM, navigerar direkt till login');
        location.href = loginHref(opts.returnTo);
        return;
    }
    const variant = texts[opts.variant] ? opts.variant : 'favorite';
    const c = texts[variant] || { title: '', body: '' };

    dom.title.textContent = c.title || '';
    dom.body.textContent = c.body || '';
    dom.cta.setAttribute('href', loginHref(opts.returnTo));
    pendingProceed = typeof opts.onProceed === 'function' ? opts.onProceed : null;

    lastFocused = document.activeElement;
    dom.root.hidden = false;
    // reflow så transitionen körs
    void dom.root.offsetWidth;
    dom.root.classList.add('is-open');
    document.documentElement.classList.add('bf-login-modal-open');
    document.addEventListener('keydown', onKeydown);
    requestAnimationFrame(() => dom.card && dom.card.focus());
    debug.log('open', variant);
}

function close() {
    const dom = collect();
    if (!dom || dom.root.hidden) return;
    dom.root.classList.remove('is-open');
    document.documentElement.classList.remove('bf-login-modal-open');
    document.removeEventListener('keydown', onKeydown);
    pendingProceed = null;
    const finish = () => {
        dom.root.hidden = true;
        dom.card.removeEventListener('transitionend', finish);
    };
    dom.card.addEventListener('transitionend', finish);
    // Fallback ifall transitionend inte fyrar
    setTimeout(finish, 400);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
}

function init() {
    const dom = collect();
    if (!dom) return;

    // Stäng: X-knapp och scrim (båda bär data-bf-login-modal-close).
    dom.root.addEventListener('click', (e) => {
        if (e.target.closest('[data-bf-login-modal-close]')) {
            e.preventDefault();
            close();
        }
    });

    // CTA: låt navigeringen ske (ingen preventDefault) men kör pendingProceed först,
    // t.ex. bf-saved.savePending. bf-upholsterys fångst-lyssnare i capture-fasen har
    // redan kört rememberReturn på samma klick, så return + replay bevaras.
    dom.cta.addEventListener('click', () => {
        if (pendingProceed) {
            try {
                pendingProceed();
            } catch (e) {
                debug.error('onProceed failade', e);
            }
            pendingProceed = null;
        }
    });

    // Deklarativ gate för Välj tyg / Visualisera. Fångas i capture-fasen så vi hinner
    // före temats egna länk-lyssnare, och bara när besökaren är utloggad.
    document.addEventListener(
        'click',
        (e) => {
            const gate = e.target.closest('[data-bf-login-gate]');
            if (!gate) return;
            if (isLoggedIn()) return; // inloggad: låt den riktiga länken gå
            const variant = gate.getAttribute('data-bf-login-gate') || 'favorite';
            let returnTo = gate.getAttribute('data-bf-login-return') || undefined;
            e.preventDefault();
            e.stopPropagation();
            open({ variant, returnTo });
        },
        true
    );

    debug.log('init');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

window.bfLoginModal = { open, close, isLoggedIn };
